import { BookOpen } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import type { ParentChildSummary } from '../../types/overview';
import { OverviewCard } from './OverviewCard';

export interface ChildrenListProps {
  summaries: ParentChildSummary[];
  /** Which child the rest of the screen is about, as the rail's switcher has it. */
  selectedUuid: string | null;
}

export function ChildrenList({ summaries, selectedUuid }: ChildrenListProps) {
  return (
    <OverviewCard
      title="My Children"
      icon={<BookOpen className="h-4 w-4 text-amber-500 dark:text-amber-400" />}
      count={summaries.length}
      viewAllTo={summaries.length > 0 ? "/parent/profile" : undefined}
      viewAllLabel="View your child's profile"
    >
      {summaries.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-5 w-5" />}
          title="No children linked yet"
          description="Children the school office links to your account will appear here."
          className="py-8"
        />
      ) : (
        summaries.map(({ child, classTeacher }) => (
          <article
            key={child.uuid}
            className={cn(
              'flex items-center justify-between gap-3 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 px-3.5 py-3 transition-all hover:border-slate-300 dark:hover:border-slate-700',
              child.uuid === selectedUuid &&
                'ring-1 ring-indigo-500/30 border-indigo-500/40 dark:border-indigo-500/40',
            )}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-purple-500/15 text-purple-600 dark:text-purple-400"
                aria-hidden
              >
                <BookOpen className="h-6 w-6" />
              </span>

              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="min-w-0 truncate text-base font-bold text-slate-900 dark:text-white">
                    {child.className}
                  </p>
                  {child.uuid === selectedUuid && (
                    <span className="rounded-full bg-indigo-500/15 px-2 py-0.5 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                      Active
                    </span>
                  )}
                </div>

                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
                  {child.fullName}
                </p>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 truncate pt-0.5">
                  {[child.rollNumber ? `Roll ${child.rollNumber}` : null, classTeacher ? `Class teacher: ${classTeacher}` : null]
                    .filter(Boolean)
                    .join(' · ') || 'Student'}
                </p>
              </div>
            </div>
          </article>
        ))
      )}
    </OverviewCard>
  );
}
